import React, { useEffect } from 'react';
import { Box, Container, Grid, useMediaQuery, useTheme } from '@mui/material';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../hooks';

import {
  getSubmissionsByCourse,
  getSubmissionsByStudent,
} from '../../../store/V2/SubmissionsSlice/api';
import {
  getSubmissionsV2State,
  ICourseSubmissionsData,
  IStudentSubmissionsData,
  resetCourseSubmissionsData,
  resetGroupedSubTopicsSubmissions,
  resetStudentSubmissionsData,
} from '../../../store/V2/SubmissionsSlice';
import TopicsAccordion from './TopicsAccordion';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import NoData from '../../../components/V2/Empty/NoData';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';

const constructBreadcrumbOptions = (
  studentData?: IStudentSubmissionsData,
  courseData?: ICourseSubmissionsData
) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'link',
      title: 'Quiz Performance',
      link: `${process.env.PUBLIC_URL}/portal/quiz-performance`,
    },
  ];
  if (courseData?.courseTitle) {
    options.push({
      type: 'link',
      title: courseData.courseTitle,
      link: `${process.env.PUBLIC_URL}/portal/quiz-performance?course=${courseData.courseId}`,
    });
  }
  if (studentData?.firstname && studentData?.lastname) {
    options.push({
      type: 'text',
      title: `${studentData?.firstname} ${studentData?.lastname}`,
      highlight: true,
    });
  }
  return options;
};

const QuizStudentPerformance: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const urlParams = useParams();

  const { courseId, studentId } = urlParams;

  // Store
  const {
    courseSubmissionsData,
    studentSubmissionsData,
    status,
  } = useAppSelector(getSubmissionsV2State);

  const breadcrumbOptions = constructBreadcrumbOptions(
    studentSubmissionsData,
    courseSubmissionsData
  );

  useEffect(() => {
    if (courseId && studentId) {
      dispatch(getSubmissionsByCourse(courseId));
      dispatch(
        getSubmissionsByStudent({
          courseId,
          studentId,
        })
      );
    }

    return () => {
      dispatch(resetStudentSubmissionsData());
      dispatch(resetCourseSubmissionsData());
      dispatch(resetGroupedSubTopicsSubmissions());
    };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ marginBottom: '24px' }}>
      <Box padding="40px 0">
        <Breadcrumbs
          options={breadcrumbOptions}
          textColor="#647488"
          showDropDown={isXsScreen}
        />
      </Box>
      <Container maxWidth="md">
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {!studentSubmissionsData && status === 'loading' ? (
              <BasicLoader
                containerProps={{
                  minHeight: '300px',
                  alignItems: 'center',
                }}
              />
            ) : null}

            {studentSubmissionsData?.groupedSubmissions.length === 0 ||
            status === 'rejected' ? (
              <NoData title="No submissions found" />
            ) : null}

            {studentSubmissionsData?.groupedSubmissions.length ? (
              <TopicsAccordion studentId={studentId} />
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </Container>
  );
};

export default QuizStudentPerformance;
